import { Core } from "@faire/design-tokens";
import {
  commonStylesForHideTopNav,
  inlineTransitionStylesForHideTopNav,
} from "@faire/retailer-shared/navbar/styles";
import { useStickyNavContentPositions } from "@faire/retailer-shared/navbar/useStickyNavContentPositions";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import React, { useRef, useState } from "react";
import styled, { css } from "styled-components";

import { filterRowDisplayStyles } from "@faire/retailer/components/Filters/filterRowDisplayStyles";
import { ScrollWrapper } from "@faire/retailer/components/Scroll/ScrollWrapper";
import { useIsLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { useTopOfPageContentStore } from "@faire/retailer/stores/ui/TopOfPageContentStore";
import { useIsLoggedOutDiscoverPage } from "@faire/retailer/views/search-v2/SearchPageQueryParams/useIsLoggedOutDiscoverPage";

//When the dragging distance is less than 10, the carousel will not scroll, but instead, consider as a click event.
const DRAGGABLE_THRESHOLD = 10;

export const MaybeDraggableStickyRowForDiscoverPage: React.FC<{
  children: React.ReactNode;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
  isLoading?: boolean;
}> = ({ children, className, isLoading, setIsDragging }) => {
  const isLoggedOutDiscover = useIsLoggedOutDiscoverPage();
  const { heightWithHeaderOrMarketBanner } = useTopOfPageContentStore([
    "heightWithHeaderOrMarketBanner",
  ]);
  const { shouldRetainOriginalPosition } =
    useStickyNavContentPositions(isLoading);
  const isLoggedInRetailer = useIsLoggedInRetailer();
  const stickyContentRef = useRef<HTMLDivElement>(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [mouseDownRelativeXCoordinate, setMouseDownRelativeXCoordinate] =
    useState<number | undefined>();
  const [scrollLeft, setScrollLeft] = useState<number | undefined>();

  const handleMouseDown = (isDown: boolean) => {
    setIsMouseDown(isDown);
  };

  const onMouseDown = (event: React.MouseEvent) => {
    const currentElement = stickyContentRef.current;
    if (!currentElement) {
      return;
    }
    handleMouseDown(true);
    const { x } = currentElement.getBoundingClientRect();
    // record starting x coordinate relative to carousel
    setMouseDownRelativeXCoordinate(event.pageX - x);
    setScrollLeft(currentElement.scrollLeft);
  };

  const onMouseMove = (event: React.MouseEvent) => {
    if (!isMouseDown) {
      return;
    }
    const currentElement = stickyContentRef.current;
    if (
      !currentElement ||
      mouseDownRelativeXCoordinate === undefined ||
      scrollLeft === undefined
    ) {
      return;
    }
    event.preventDefault();
    const { x } = currentElement.getBoundingClientRect();
    // calculate mouse x coordinate relative to the carousel
    const mouseRelativeXCoordinate = event.pageX - x;
    // calculate distance mouse moved
    const movingDistance =
      mouseRelativeXCoordinate - mouseDownRelativeXCoordinate;
    if (Math.abs(movingDistance) < DRAGGABLE_THRESHOLD) {
      return;
    }
    // scroll carousel based on the distance mouse moved
    setIsDragging(true);
    currentElement.scrollTo?.({
      left: scrollLeft - movingDistance,
    });
  };

  if (isLoggedOutDiscover) {
    return (
      <DiscoverStickyRow
        ref={stickyContentRef}
        className={className}
        $top={heightWithHeaderOrMarketBanner}
        style={inlineTransitionStylesForHideTopNav(
          shouldRetainOriginalPosition,
          heightWithHeaderOrMarketBanner
        )}
        $isLoggedOut={!isLoggedInRetailer}
        onMouseDown={onMouseDown}
        onMouseLeave={() => handleMouseDown(false)}
        onMouseUp={() => {
          handleMouseDown(false);
          setIsDragging(false);
        }}
        onMouseMove={onMouseMove}
      >
        {children}
      </DiscoverStickyRow>
    );
  }
  return (
    <StickyRow
      ref={stickyContentRef}
      className={className}
      $top={heightWithHeaderOrMarketBanner}
      style={inlineTransitionStylesForHideTopNav(
        shouldRetainOriginalPosition,
        heightWithHeaderOrMarketBanner
      )}
      $isLoggedOut={!isLoggedInRetailer}
    >
      {children}
    </StickyRow>
  );
};

const StickyRow = styled.div<{
  $top?: number;
  $isLoggedOut?: boolean;
}>`
  position: sticky;
  top: ${({ $top }) => $top ?? 0}px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  left: 0;
  z-index: 10;
  background-color: ${Core.surface.inverse};

  &:empty {
    display: none;
  }

  ${responsiveMediaQueries.tabletAndBelow} {
    ${({ $isLoggedOut }) =>
      !$isLoggedOut
        ? css`
            width: 100vw;
          `
        : null}
    padding: ${getSpacing(0, "2x")};
    margin: ${getSpacing(0, "-2x")};
  }

  ${commonStylesForHideTopNav}
  ${filterRowDisplayStyles}
`;

const DiscoverStickyRow = styled(ScrollWrapper)<{
  $top?: number;
  $isLoggedOut?: boolean;
}>`
  position: sticky;
  top: ${({ $top }) => $top ?? 0}px;
  overflow-x: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  left: 0;
  z-index: 10;
  background-color: ${Core.surface.inverse};

  &:empty {
    display: none;
  }

  &:hover {
    cursor: grab;
  }

  &:active {
    cursor: grabbing;
  }

  ${responsiveMediaQueries.tabletAndBelow} {
    ${({ $isLoggedOut }) =>
      !$isLoggedOut
        ? css`
            width: 100vw;
          `
        : null}
    padding: ${getSpacing(0, "2x")};
    margin: ${getSpacing(0, "-2x")};
  }

  ${commonStylesForHideTopNav}
  ${filterRowDisplayStyles}
`;
